import React, { useCallback, useEffect, useState } from "react";
import "devextreme/dist/css/dx.light.css";
import "./Environments.scss";
import CustomStore from "devextreme/data/custom_store";
import { Tab, Tabs } from "react-bootstrap";
import { Button } from "devextreme-react/button";
import PopUp from "devextreme-react/popup";
import { LoadPanel } from "devextreme-react/load-panel";
import PropTypes from "prop-types";
import {
  ApiSuccessMsgs,
  ClinithinkRequest,
  resRoles,
  Roles,
  Status,
} from "../../Constants/constants";
import { addEnvSVG, reqEnvSVG } from "../../Assets/SVGs/environmentSVGs";
import EnvironmentDataGrid from "../EnvironmentDatagrid/EnvironmentDatagrid";
import Notification from "../Notification/Notification";
import { orgDataPropTypes } from "../../Constants/propTypes";
import { useUser } from "../../contexts/UserContext";
import CreateEditEnvironment from "../CreateEditEnvironment/CreateEditEnvironment";
import SubscriptionTab from "../SubscriptionTab/SubscriptionTab";

const Environments = ({ orgData, setViewDataReceiptPage, setEnvData }) => {
  const { userRoles, backend } = useUser();

  const [customStore, setCustomStore] = useState(null);
  const [envCount, setEnvCount] = useState("");
  const [maxEnvCount, setMaxEnvCount] = useState("");
  const [showEnvData, setShowEnvData] = useState(false);
  const [tabsActiveKey, setTabsActiveKey] = useState(1);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [mode, setMode] = useState("Create");
  const [selectedEnvData, setSelectedEnvData] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const isSuperUser = userRoles?.includes(Roles.SUPER_USER);
  const isOrgAdmin = userRoles?.includes(Roles.ORG_ADMIN);
  const isEngineReader = userRoles?.includes(Roles.ENG_READER_ROLE);
  const isOperationalReader = userRoles?.includes(Roles.OP_READER_ROLE);

  const sendRequest = useCallback(
    // eslint-disable-next-line consistent-return
    async (type, envDetails) => {
      if (envDetails !== undefined) {
        if (type === "load") {
          const data = await backend.callAllEnvByOrgId(envDetails).then((res) => res);
          setEnvCount(data.data.environments.length);
          setMaxEnvCount(data.data.maxEnvironmentsLimit);
          return data;
        }

        if (type === "update" && envDetails) {
          return backend.callUpdateEnvironment(envDetails.id, envDetails);
        }

        if (type === "delete" && envDetails) {
          return backend.callDeleteEnvironment(envDetails.id);
        }
      }
    },
    [backend]
  );

  useEffect(() => {
    setTabsActiveKey(1);
    setCustomStore(
      new CustomStore({
        load: async () =>
          sendRequest("load", orgData?.id).then((res) => res.data.environments),
        update: (key, values) => {
          // eslint-disable-next-line no-param-reassign
          values = { ...key, ...values };
          return sendRequest("update", values);
        },
        remove: (key) => sendRequest("delete", key),
      })
    );
  }, [orgData, sendRequest]);

  const onTabClick = useCallback((key) => {
    if (key === "1") {
      setTabsActiveKey(1);
      setShowEnvData(true);
    }
    if (key === "2") {
      setTabsActiveKey(2);
      setShowEnvData(false);
    }
  }, []);

  const togglePopup = useCallback(() => {
    setIsPopupVisible(!isPopupVisible);
  }, [isPopupVisible]);

  const copyToClipboard = useCallback((e) => {
    const copyText = e.row.data.appRegistrationId;
    navigator.clipboard.writeText(copyText);
    Notification(ApiSuccessMsgs.ClientIDCopied, "success");
  }, []);

  const formatStatus = useCallback(
    (cellValue) => (cellValue.value ? Status.ACTIVE : Status.IN_ACTIVE),
    []
  );

  const viewDataReceipt = useCallback(
    (e) => {
      backend
        .callDataRecieptFileTypesForDataReceipts(e.row.data.id, 1, 1)
        .then((res) => {
          setIsLoadingData(false);
          if (res.data === null) {
            Notification(res.message, "error");
          } else {
            setEnvData(e.row.data);
            setViewDataReceiptPage(true);
          }
        })
        .catch((err) => {
          Notification(err.response.data.message, "error");
        });
    },
    [backend, setEnvData, setViewDataReceiptPage]
  );

  const renderEnvironmentPopup = useCallback(
    () => (
      <CreateEditEnvironment
        mode={mode}
        setMode={setMode}
        environmentData={selectedEnvData}
        setEnvironmentData={setSelectedEnvData}
        orgId={orgData?.id}
        isPopupVisible={isPopupVisible}
        closePopup={togglePopup}
        setIsLoadingData={setIsLoadingData}
        setCustomStore={setCustomStore}
        sendRequest={sendRequest}
      />
    ),
    [isPopupVisible, mode, orgData?.id, selectedEnvData, sendRequest, togglePopup]
  );

  const handleOpenEditEnvironmentPopup = useCallback((e) => {
    setSelectedEnvData(e.row.data);
    setMode("Edit");
    setIsPopupVisible(true);
  }, []);

  const renderAddEnvironmentBtnWithInfo = () =>
    isSuperUser ? (
      <div className="d-flex env-count-and-add-button">
        <div className="environemnts-count">{envCount} environments</div>
        <div className="create-environment-button">
          <Button
            className="add-environment-button"
            onClick={togglePopup}
            stylingMode="text"
          >
            {addEnvSVG} Environment
          </Button>
        </div>
      </div>
    ) : (
      <div className="d-flex env-count-and-add-button">
        <div className="environemnts-count">
          {envCount} of {maxEnvCount} environments
        </div>
        <div className="create-environment-button">
          {isOrgAdmin && maxEnvCount > envCount && (
            <Button
              className="add-environment-button"
              onClick={togglePopup}
              stylingMode="text"
            >
              {addEnvSVG} Environment
            </Button>
          )}

          {isOrgAdmin && maxEnvCount <= envCount && (
            <div className="req-env-div">
              <span className="req-env-svg">{reqEnvSVG}</span>
              <span className="req-env">{ClinithinkRequest.requestMsg}</span>
            </div>
          )}
        </div>
      </div>
    );

  const environtmentTab = () => (
    <div id="environments-div-datagridenvironment">
      <div>
        <div className="environment-data-grid">
          <div className="environment-inner-content">
            <div className="d-flex flex-column">
              <div className="manage-details d-flex">
                <div className="env-details">
                  <i>Manage your environment details</i>
                </div>
                {renderAddEnvironmentBtnWithInfo()}
              </div>
              <div>
                <div>
                  <PopUp
                    visible={isPopupVisible}
                    position="center"
                    maxHeight={mode === "Create" ? 660 : 580}
                    maxWidth={836}
                    showTitle={false}
                    showCloseButton={false}
                    contentRender={renderEnvironmentPopup}
                    wrapperAttr={{ class: "create-edit-environment-popup-wrapper" }}
                  />
                </div>
                <EnvironmentDataGrid
                  customStore={customStore}
                  copyToClipboard={copyToClipboard}
                  formatStatus={formatStatus}
                  supRole={isSuperUser}
                  orgRole={isOrgAdmin}
                  toggleEditEnvs={handleOpenEditEnvironmentPopup}
                  engineReaderRole={isEngineReader}
                  operationalReaderRole={isOperationalReader}
                  viewDataReceipt={viewDataReceipt}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <br />
    </div>
  );

  return (
    <div className="environmentTile">
      {(showEnvData || orgData) && (
        <>
          <LoadPanel position="center" visible={isLoadingData} showPane={true} />
          <div className="environment-tabs">
            <Tabs activeKey={tabsActiveKey} id="environments-tabs" onSelect={onTabClick}>
              <Tab eventKey={1} className="environments-tab" title="Environment details">
                <div>{environtmentTab()}</div>
              </Tab>
              {(userRoles.includes(resRoles.resMangerRole) ||
                userRoles.includes(resRoles.resReadRole) ||
                userRoles.includes(resRoles.resWriteRole)) && (
                <Tab eventKey={2} className="resources-tab" title="Subscribe resources">
                  <SubscriptionTab orgData={orgData} />
                </Tab>
              )}
            </Tabs>
          </div>
        </>
      )}
    </div>
  );
};

Environments.propTypes = {
  orgData: orgDataPropTypes.isRequired,
  setViewDataReceiptPage: PropTypes.func.isRequired,
  setEnvData: PropTypes.func.isRequired,
};
export default Environments;
